import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import React, { useState } from "react";
import { usePostProjectByProjectIdIssuesCreateMutation } from "../../../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { selectIssueBoard } from "../../issueSelectors";
import { thunkOpenIssueModalById } from "../../issueSlice";
import {
  selectConnectedBimSpaceForSelectedFloorSpace,
  selectHiddenIssueCountForSelectedFloorSpace,
  selectIssuesForSelectedFloorSpace,
  selectSelectedFloorSpace,
} from "./floorplanSelectors";
import { setSelectedFloorSpaceIdInRedux } from "./floorplanSlice";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";

const FloorplanSelectedSpacePanel: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newIssueTitle, setNewIssueTitle] = useState("");
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.adminReducer.project);
  const issueBoard = useAppSelector(selectIssueBoard);
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const selectedSpace = useAppSelector(selectSelectedFloorSpace);
  const connectedBimSpace = useAppSelector(selectConnectedBimSpaceForSelectedFloorSpace);
  const issues = useAppSelector(selectIssuesForSelectedFloorSpace);
  const hiddenIssueCount = useAppSelector(selectHiddenIssueCountForSelectedFloorSpace);
  const [createIssue, { isLoading: isCreatingIssue }] = usePostProjectByProjectIdIssuesCreateMutation();

  if (selectedSpace == null) {
    return null;
  }

  const canCreateIssue =
    project != null &&
    issueBoard != null &&
    connectedBimSpace != null &&
    newIssueTitle.trim() !== "" &&
    !isCreatingIssue;

  const handleCreateIssue = async () => {
    if (!canCreateIssue || project == null || issueBoard == null || connectedBimSpace == null) {
      return;
    }

    const createdIssueResp = await createIssue({
      projectId: project._id,
      body: {
        issueBoardId: issueBoard._id,
        title: newIssueTitle.trim(),
        connectedBimSpaces: [connectedBimSpace],
      },
    }).unwrap();

    setNewIssueTitle("");
    setIsCreateModalOpen(false);
    dispatch(thunkOpenIssueModalById(createdIssueResp._id));
  };

  return (
    <>
      <aside
        style={{
          width: 292,
          flexShrink: 0,
          alignSelf: "stretch",
          borderLeft: "1px solid var(--mantine-color-gray-3)",
          overflow: "hidden",
          background: "var(--mantine-color-gray-0)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Group
          justify="space-between"
          align="flex-start"
          wrap="nowrap"
          style={{
            padding: "0.75rem 1rem",
            background: "var(--mantine-color-gray-1)",
            borderBottom: "1px solid var(--mantine-color-gray-3)",
          }}
        >
          <Box>
            <Text fw={700}>{selectedSpace.name || "Uten romnummer"}</Text>

            {selectedSpace.longName && (
              <Text size="sm" c="dimmed" mt={2}>
                {selectedSpace.longName}
              </Text>
            )}
          </Box>

          <Tooltip label="Fjern markering" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => {
                if (issueBoardId == null) return;

                dispatch(setSelectedFloorSpaceIdInRedux({ issueBoardId, selectedFloorSpaceId: "" }));
              }}
            >
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Stack gap="md" style={{ padding: "1rem", minHeight: 0, flex: 1 }}>
          <Button
            fullWidth
            color="mc-primary"
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Ny sak
          </Button>

          <Stack
            gap="xs"
            style={{
              background: "white",
              border: "1px solid var(--mantine-color-gray-2)",
              borderRadius: "6px",
              padding: "0.875rem",
              minHeight: 0,
            }}
          >
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text size="sm" fw={600}>
                Saker i rommet
              </Text>

              {hiddenIssueCount > 0 && (
                <Tooltip label="Disse sakene er koblet til rommet, men skjules av aktive filtre" withArrow>
                  <Badge color="yellow" variant="light">
                    {hiddenIssueCount} skjult
                  </Badge>
                </Tooltip>
              )}
            </Group>

            {issues.length === 0 && (
              <Text size="sm" c="dimmed" lh={1.45}>
                Ingen saker koblet til dette rommet.
              </Text>
            )}

            {issues.map((issue) => (
              <UnstyledButton
                key={issue._id}
                onClick={() => dispatch(thunkOpenIssueModalById(issue._id))}
                style={{
                  display: "block",
                  width: "100%",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "6px",
                  padding: "0.75rem",
                  textAlign: "left",
                  background: "var(--mantine-color-gray-0)",
                }}
              >
                <Stack gap={6}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text size="sm" fw={700}>
                      {issue.id || issue._id}
                    </Text>

                    <Group gap={6} wrap="nowrap" align="center">
                      <Box className="statusdot" style={{ backgroundColor: issue.statusColor }} />
                      <Text size="xs" c="dimmed">
                        {issue.status}
                      </Text>
                    </Group>
                  </Group>

                  <Text size="sm" lineClamp={2}>
                    {issue.title || "Uten tittel"}
                  </Text>
                </Stack>
              </UnstyledButton>
            ))}
          </Stack>
        </Stack>
      </aside>

      <Modal opened={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Ny sak" size="sm">
        <Stack gap="sm">
          <TextInput
            data-autofocus
            placeholder="Skriv inn navn på sak"
            value={newIssueTitle}
            onChange={(event) => setNewIssueTitle(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canCreateIssue) {
                event.preventDefault();
                void handleCreateIssue();
              }
            }}
          />

          <Group justify="space-between">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Avbryt
            </Button>
            <Button color="mc-primary" onClick={() => void handleCreateIssue()} disabled={!canCreateIssue}>
              Opprett
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default FloorplanSelectedSpacePanel;
